import { Injectable } from '@angular/core';

import { CACHE_NAME, CACHE_LIFESPAN } from '@core/configs';

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  /**
   * Usage: const cacheRequest = this.cacheService.createCacheRequest(API_ENDPOINT.corp.controller, { method: 'GET' });
   */
  createCacheRequest(url: string, init?: RequestInit): Request {
    return new Request(url, init);
  }

  /**
   * Usage: const cache = await this.cacheService.open();
   */
  async open(cacheName?: string): Promise<Cache> {
    const cacheNameUse = cacheName || CACHE_NAME;
    return await caches.open(cacheNameUse);
  }

  /**
   * Usage: const cachedResponse = await this.cacheService.getCachedResponse(cache, cacheRequest);
   */
  async getCachedResponse(cache: Cache, cacheRequest: Request, cacheLifespan?: number): Promise<any> {
    const now = Date.now();
    const cachedResponse = await cache.match(cacheRequest);
    if (cachedResponse) {
      const cachedTime = Number(cachedResponse.headers.get('Cache-Time') || 0);
      const lifespan = cacheLifespan || CACHE_LIFESPAN.defaultTTL;
      if (cachedTime + lifespan > now) {
        console.debug('[CacheService] Return data from cache'); // NOSONAR
        return await cachedResponse.clone().json();
      } else {
        console.debug('[CacheService] CACHE_EXPIRED - Delete cache'); // NOSONAR
        await cache.delete(cacheRequest);
      }
    }
    return undefined;
  }

  /**
   * Usage: await this.cacheService.put(cache, cacheRequest, res);
   */
  async put(cache: Cache, cacheRequest: Request, response: any): Promise<void> {
    const now = Date.now();
    const headers = new Headers();
    headers.set('Cache-Time', now.toString());
    headers.set('Content-Type', 'application/json');
    const responseToCache = new Response(JSON.stringify(response), {
      headers,
    });
    console.debug('[CacheService] Put response to cache'); // NOSONAR
    return await cache.put(cacheRequest, responseToCache);
  }

  /**
   * Usage: await this.cacheService.delete(cache, cacheRequest);
   */
  async delete(cache: Cache, cacheRequest: Request): Promise<boolean> {
    console.debug('[CacheService] Delete cache'); // NOSONAR
    return await cache.delete(cacheRequest);
  }

  /**
   * Usage for no await: this.cacheService.clearAll().catch((e) => console.error(e)); // NOSONAR
   */
  async clearAll(): Promise<void> {
    try {
      const cacheKeys = await caches.keys();
      const deletionPromises = cacheKeys.map(key => {
        return caches.delete(key);
      });
      if (deletionPromises?.length) {
        console.debug('[CacheService] Function clearAll - run delete'); // NOSONAR
        await Promise.all(deletionPromises);
      }
    } catch (e) {
      console.error(e); // NOSONAR
      // try delete cache default
      caches.delete(CACHE_NAME).catch(e2 => {
        console.error(e2); // NOSONAR
      });
      throw e;
    }
  }
}
